import React, { useEffect, useState } from 'react';
import { billingService, getErrorMessage } from '../services/api';
import type { BillingInfo } from '../types/api';
import { notePaymentsConfigured, openBillingPortal } from '../utils/checkout';
import { formatDailyTime, formatDate } from '../utils/format';
import { useToast } from './Toast';
import PaymentsNote from './PaymentsNote';
import TrialRequest from './TrialRequest';
import UpgradeButton from './UpgradeButton';

// The student's own plan (PLAN-13): Free with the way to the Student plan, or the Student plan and until when. Time in a
// paid class's lessons comes from the class (PLAN-3), which this line doesn't count.
const PlanLine: React.FC = () => {
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const toast = useToast();
  useEffect(() => {
    billingService.me().then((loaded) => { notePaymentsConfigured(loaded); setInfo(loaded); }).catch(() => setInfo(null));
  }, []);
  if (!info || info.plan === 'admin') return null;
  const minutes = formatDailyTime;
  if (info.plan === 'student') {
    return (
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        <span className="badge badge-green mr-1 align-[1px]">Student plan</span> · {minutes(info.limits.student_seconds_per_day)} of simulation a day
        {info.plan_period_end && <> · renews or ends {formatDate(info.plan_period_end)}</>}
        {info.checkout?.can_manage && (
          <>
            {' · '}
            <button
              type="button"
              className="link font-semibold"
              onClick={() => openBillingPortal().catch((error) => toast.error(getErrorMessage(error)))}
            >
              Manage subscription
            </button>
          </>
        )}
        <PaymentsNote as="span" className="mt-1 block text-xs text-gray-500 dark:text-gray-500" />
      </p>
    );
  }
  return (
    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
      <span className="badge badge-gray mr-1 align-[1px]">Free</span> · {minutes(info.limits.free_seconds_per_day)} of simulation a day (your classes' lessons may have more)
      {info.checkout ? (
        <>
          {' · '}
          <UpgradeButton plan="student" className="link font-semibold text-left">
            Student plan: {minutes(info.limits.student_seconds_per_day)} a day for €{info.prices.student.amount}/month
          </UpgradeButton>
        </>
      ) : (
        // PLAN-18: until online payment is on, a free month instead.
        <TrialRequest
          plan="student"
          as="span"
          className="mt-1 block"
          offer={<>Student plan: {minutes(info.limits.student_seconds_per_day)} a day for €{info.prices.student.amount}/month.</>}
          noteClassName="text-gray-600 dark:text-gray-400"
          buttonClassName="btn btn-primary btn-sm"
        />
      )}
      <PaymentsNote as="span" className="mt-1 block text-xs text-gray-500 dark:text-gray-500" />
    </p>
  );
};

export default PlanLine;
